import { useContext, useEffect, useState, useRef } from 'react';
import {
  Button, Grid, TextField, Checkbox, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, Typography,
  SvgIcon, InputAdornment, Autocomplete, Divider,
  useMediaQuery, Box, Tooltip, useTheme, Tabs, Tab, Dialog,
  DialogActions, DialogContent, DialogTitle,
  MenuItem,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import { SearchSm } from '../../../assets';
import { OrderTypeTranslationMap } from '../../../enums/OrderType';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { client, SimpleCategory, OrderDetails, OrderItemDetails, DeleteOrderItemsRequest, OrderlySystem, ItemDocument, ContainerFillResult, OrderUpdate, OrderItem, OrderItemUpdate, DeliveryOptions } from '../../../app/services/api/orderManagementClient';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import CopyOrderModal from '../../../components/shared/orders/CopyOrderModal';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { OrderStatus } from '../../../enums/OrderStatus';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { applyRoundAndFormat, exportFile } from '../../../utils';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import BackButton from '../../../components/shared/actions/BackButton';
import { useTranslation } from 'react-i18next';
import TextWithOverflowTooltip from '../../../components/shared/TextWithOverflowTooltip';
import { starbucksFranchises } from '../../../enums/FranchiseNames';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import './OrderDetails.css';
import { QuantityWarningDialog } from '../../../components/shared/orders/QuantityWarningDialog';
import OrderAuditModal from '../../../components/shared/orders/OrderAuditModal';
import ContainerPlanningView from './ContainerPlanningView';
import useLogError from '../../../hooks/useLogError';
import NoProductImage from '../../../assets/images/NoProductImage.png';
import OrderStatusChip from '../../../components/shared/orders/OrderStatusChip';
import useNswagClient from '../../../hooks/api/useNswagClient';
import OrderDocuments from '../OrderDetailsOfd/OrderDocuments';
import { downloadFile } from '../ProductDetails/utils/ProductDocumentUtils';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ActionConfirmationDialog } from '../../../components/shared/dialogs/ActionConfirmationDialog';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { useActiveOrder } from '../../../contexts/ActiveOrderContext';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import ItemPriceOverrideModal from './modals/ItemPriceOverrideModal';
import { OnboardingPageData, Int32StringKeyValuePair } from '../../../app/services/api/ofdClient';

enum PriceOverrideReason {
  PromoOffer = 1,
  AccountManagerDecision = 2
}

const PriceOverrideReasonText: Record<number, string> = {
  [PriceOverrideReason.PromoOffer]: 'Promo Offer',
  [PriceOverrideReason.AccountManagerDecision]: 'Account Manager Decision',
};

interface DialogState {
  open: boolean;
  title: string;
  message: string;
  confirmAction: () => void;
}

const enum OrderDetailsEnum {
  Basket = 1,
  Shipping = 2,
  Documents = 3,
}

const OrderDetailsPageOfd = () => {
  dayjs.extend(duration);
  const theme = useTheme();
  const navigate = useNavigate();
  const { logError } = useLogError();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { t } = useTranslation('orderDetails');
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { setActiveOrder, setContainerData: setActiveContainerData, setShippingDetails } = useActiveOrder();
  const { getOnboardingPageData } = useNswagClient();

  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState<string>('');

  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState<number>(-1);

  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>();
  const [filteredItems, setFilteredItems] = useState<OrderItemDetails[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<SimpleCategory[]>([]);
  const [itemQuantities, setItemQuantities] = useState<{ [key: number]: number }>({});
  const [isCopyOrderModalOpen, setIsCopyOrderModalOpen] = useState(false);
  const [isOrderAuditModalOpen, setIsOrderAuditModalOpen] = useState(false);

  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);

  const [deliveryMethod, setDeliveryMethod] = useState<string>('Ex Works');
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptions[]>([]);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<string>('');
  const [deliveryCharge, setDeliveryCharge] = useState<string>('0.00');

  const [country, setCountry] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [containerType, setContainerType] = useState<number | string>('');
  const [palletType, setPalletType] = useState<number | string>('');

  const INACTIVITY_TIMEOUT = 60000;

  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);
  const [isExitAndSaveClicked, setIsExitAndSaveClicked] = useState<boolean>(false);

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');

  const { downloadProductDocument } = useNswagClient();
  const [productDocuments, setProductDocuments] = useState<(ItemDocument & { productId: number, name: string; sku: string })[]>([]);
  const [containerData, setContainerData] = useState<ContainerFillResult | null>(null); const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Dayjs | null>(null);

  const [isDownloadingSpreadsheet, setIsDownloadingSpreadsheet] = useState<boolean>(false);
  const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = useState<boolean>(false);
  const [handleShowOverridePrice, setHandleShowOverridePrice] = useState<boolean>(false);
  const [selectedOverrideItem, setSelectedOverrideItem] = useState<OrderItemDetails | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [containerSizes, setContainerSizes] = useState<Int32StringKeyValuePair[]>([]);

  let totalCost = 0;

  const [tabValue, setTabValue] = useState<OrderDetailsEnum>(OrderDetailsEnum.Basket);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    title: '',
    message: '',
    confirmAction: () => { },
  });

  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);

  const [checkoutDialogData, setCheckoutDialogData] = useState({
    title: '',
    message: '',
    confirmAction: () => { },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const { id } = useParams();

  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && orderDetails?.status != 'Order Confirmed';

  const blocker = useBlocker(() => modifiedItems.size > 0 && !isExitAndSaveClicked);

  const canCheckoutOrder = (): boolean => {
    if (!containerData || containerData.containerFillPercents.length === 0) {
      return false;
    }
    const lastContainerFill = containerData.containerFillPercents[containerData.containerFillPercents.length - 1];
    return lastContainerFill >= containerData.minFill && orderDetails?.status === OrderStatus.Pending;
  };

  const [rejectDialogOpen, setRejectDialogOpen] = useState<boolean>(false);

  const canEditOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending ||
      orderDetails?.status === OrderStatus.Review ||
      orderDetails?.status === OrderStatus.Amended;
  };

  const loadContainerSizes = async () => {
    getOnboardingPageData().then((result: OnboardingPageData) => {
      if (result) {
        setContainerSizes(result.containerSizes ?? []);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).catch((result: any) => {
      console.error(result);
    });
  };

  const handleEditClick = (item: OrderItemDetails) => {
    setHandleShowOverridePrice(true);
    setSelectedOverrideItem(item);
  };

  const canChangeOrderQuantity = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canDeleteOrderItem = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canChangeContainer = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canChangePalletType = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canApproveOrder = (): boolean => {
    return hasPermissionTo(['OrdersApprove']) && orderDetails?.status === OrderStatus.Review;
  };

  const canApproveAmendedOrder = (): boolean => {
    return hasPermissionTo(['OrdersApproveConfirmation']) && orderDetails?.status === OrderStatus.Amended;
  };

  const canRejectAmendedOrder = (): boolean => {
    return hasPermissionTo(['OrdersRejectConfirmation']) && orderDetails?.status === OrderStatus.Amended;
  };

  const canAmendOrderDate = (): boolean => {
    return hasPermissionTo(['OrdersChangeDeliveryDate']) && orderDetails?.status === OrderStatus.Review;
  };

  const canSaveChanges = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const displayDeliveryDate = (): boolean => {
    return orderDetails?.status === OrderStatus.Review || orderDetails?.status === OrderStatus.Amended;
  };

  const canDeleteOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending;
  };

  const isOrderEditableByStatus = (order: OrderDetails | null | undefined): boolean => {
    return order?.status === OrderStatus.Pending ||
      (hasPermissionTo(['OrdersApprove']) && order?.status === OrderStatus.Review);
  };

  const canUpdateDeliveryMethod = (): boolean => {
    return orderDetails?.status === OrderStatus.Review ||
      (hasPermissionTo(['DeliveryMethodWrite']) && orderDetails?.status === OrderStatus.Pending);
  };
  const canUpdateDeliveryCharge = (): boolean => {
    return orderDetails?.status === OrderStatus.Review ||
      (hasPermissionTo(['DeliveryChargeWrite']) && orderDetails?.status === OrderStatus.Pending);
  };
  const isOrderEditable = (): boolean => {
    return isOrderEditableByStatus(orderDetails);
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
    loadContainerSizes();
  }, []);

  useEffect(() => {
    if (orderDetails) {
      const categoriesById: Record<number, SimpleCategory> = {};
      orderDetails.items.forEach((item) => {
        if (!categoriesById[item.categoryId]) {
          categoriesById[item.categoryId] = { id: item.categoryId, name: item.category };
        }
      });
      setCategories(Object.values(categoriesById));
      if (orderDetails.deliveryDate) {
        const parsedDate = dayjs(orderDetails.deliveryDate, 'DD-MM-YYYY');
        if (parsedDate.isValid()) {
          setSelectedDeliveryDate(parsedDate);
        }
      } else {
        setSelectedDeliveryDate(null);
      }
    }
  }, [orderDetails]);

  useEffect(() => {
    if (modifiedItems.size > 0) {
      startInactivityTimer();
    }
  }, [itemQuantities]);

  useEffect(() => {
    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedItems.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedItems]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/order');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {

        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data) {
          const data = result.data;
          setDeliveryMethod(data.deliveryMethod || 'Ex Works');
          setCountry(data.country);
          setLocation(data.location);
          setContainerType(data.containerType ?? '');
          setPalletType(data.palletType ?? '');
          setDeliveryOptions(data.deliveryOptions || []);
          const chargeValue = data.deliveryCharge || 0;
          setDeliveryCharge(chargeValue.toString());

          if (data.deliveryMethodId) {
            setSelectedShippingMethodId(data.deliveryMethodId);
          } else if (data.deliveryOptions && data.deliveryOptions.length > 0) {
            const matchingOption = data.deliveryOptions.find(option =>
              option.name === data.deliveryMethod,
            );
            if (matchingOption) {
              setSelectedShippingMethodId(matchingOption.shippingMethodId.toString());
            }
          }
        }
      } 
      catch (error) {
        console.error('Failed to fetch shipping details', error);
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id) {
      fetchShippingDetails();
    }
  }, [selectedStore?.storeNumber, orderDetails?.id]);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const autosaveShippingDetails = (
    shippingMethodId?: string,
    deliveryMethodCharge?: number,
    newContainerType?: string | number,
    newPalletType?: string | number,

  ) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(async () => {
      try {
        const response = await client.setShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id.toString() ?? '',
          {
            deliveryCharge: deliveryMethodCharge ?? 0,
            deliveryMethod:  deliveryMethod,
            deliveryMethodId: shippingMethodId ?? '',
            orderStatus: orderDetails?.statusId.toString() ?? '',
            deliveryOptions: deliveryOptions,
            country,
            location,
            containerType: Number(newContainerType ?? containerType),
            palletType: Number(newPalletType ?? palletType),
          },
        );
        if (response.success) {
          handleSnackbarOpen(('Shipping details updated successfully.'), 'success');
          await fetchOrderDetails();
        }
        else {
          setWarningMessage(response.message);
        }
        await fetchOrderDetails();
      } catch (error) {
        console.error('Failed to autosave shipping details', error);
      }
    }, 500);
  };

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    handleSaveChanges();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const startInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    const timer = setTimeout(async () => {
      if (modifiedItems.size > 0) {
        modifiedItems.forEach(itemId => {
          autosaveItem(itemId);
        });
      }
    }, INACTIVITY_TIMEOUT);

    setInactivityTimer(timer);
  };

  const handleItemFocus = (itemId: number) => {
    if (lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedItems.has(lastFocusedItemId)) {

      autosaveItem(lastFocusedItemId);
    }
    setLastFocusedItemId(itemId);
  };

  const updateItemPriceOverride = async (editedItem: OrderItemUpdate) => {
    if (!orderDetails) return;

    // keep all items, update the one that matches
    const updatedItems = orderDetails.items.map(item =>
      item.itemId === editedItem.id
        ? {
          ...item,
          id: editedItem.id,
          quantity: editedItem.quantity,
          overridePrice: editedItem.overridePrice,
          overrideReason: editedItem.overrideReason,
        }
        : {
          ...item,
          id: item.itemId,
        },
    );


    const updatedOrder = {
      ...orderDetails,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFD,
    };


    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        handleSnackbarOpen(t('itemUpdated'), 'success');
        await fetchOrderDetails();
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }

    setHandleShowOverridePrice(false);
    setSelectedOverrideItem(undefined);
  };

  const autosaveItem = async (itemId: number) => {
    if (!orderDetails) return;

    const updatedItems = orderDetails.items
      .map(item => ({
        id: item.itemId,
        quantity: itemQuantities[item.itemId] ?? item.quantity,
      }));

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFD,
    };

    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        handleSnackbarOpen(t('itemQuantityUpdated'), 'success');
        setModifiedItems(prev => {
          const newModifiedItems = new Set(prev);
          newModifiedItems.delete(itemId);
          return newModifiedItems;
        });
        await fetchOrderDetails();
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedStore, id]);

  const loadData = async () => {
    setLoading(true);
    await fetchOrderDetails();
    setLoading(false);
  };

  useEffect(() => {
    if (orderDetails && Object.keys(itemQuantities).length > 0) {
      const updatedOrder = {
        ...orderDetails,
        items: orderDetails.items.map(item => ({
          ...item,
          quantity: itemQuantities[item.itemId] ?? item.quantity,
        })),
      };
      if (isOrderEditable()) {
        setActiveOrder(updatedOrder);
      }
    }
  }, [itemQuantities, orderDetails, setActiveOrder]);

  useEffect(() => {
    if (orderDetails && filteredItems.length > 0) {
      fetchItemDocuments();
    }
  }, [orderDetails, filteredItems]);

  const fetchOrderDetails = async () => {
    try {
      const result = await client.getOrder(selectedStore?.storeNumber, parseInt(id ?? ''));
      setOrderDetails(result.data);
      setFilteredItems(result.data.items);
      setOrderType(result.data.orderType);
      if (isOrderEditableByStatus(result.data)) {
        setActiveOrder(result.data);
      } else {
        setActiveOrder(null);
      }
      if (selectedStore?.storeNumber && result.data.id) {
        try {
          const containerResult = await client.getStoreContainerData(
            selectedStore.storeNumber,
            result.data.id.toString(),
          );
          setContainerData(containerResult.data);
          setActiveContainerData(containerResult.data);

          if (containerResult.data.errors && containerResult.data.errors.length > 0) {
            handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + containerResult.data.errors.join(', '), 'error');
          }
        } catch (err) {
          logError(err);
          handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + err, 'error');
        }
      }

    } catch (error) {
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const fetchItemDocuments = async () => {
    try {
      if (orderDetails && orderDetails.id) {
        const response = await client.getOrderDocuments(selectedStore?.storeNumber, orderDetails.id);
        if (response.success) {
          const allDocs: (ItemDocument & { productId: number; name: string; sku: string })[] = [];
          Object.entries(response.data).forEach(([itemId, documents]) => {
            const orderItem = filteredItems.find(item => item.itemId === parseInt(itemId));
            if (orderItem) {
              const docsWithProduct = documents.map(doc => ({
                ...doc,
                productId: orderItem.itemId,
                name: orderItem.name,
                sku: orderItem.sku,
              }));
              allDocs.push(...docsWithProduct);
            }
          });
          setProductDocuments(allDocs);
        } else {
          console.error('Failed to fetch order documents:', response.message);
        }
      }
    } catch (error) {
      console.error('Error fetching order documents:', error);
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const handleOpenDialogForClearBasket = (orderId: number) => {
    setDialogState({
      open: true,
      title: t('clearBasketModalTitle'),
      message: t('clearBasketModalMessage'),
      confirmAction: () => onClearBasket(orderId),
    });
  };

  const onClearBasket = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await client.deleteAllOrderItems(selectedStore?.storeNumber, orderId.toString());
      if (response.success) {
        handleSnackbarOpen(t('basketCleared'), 'success');
        await fetchOrderDetails();
      }
      else {
        setWarningMessage(response.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }

  };

  const handleSearch = () => {
    if (orderDetails) {
      const filtered = orderDetails.items.filter(item => {
        const matchesSearchText = productSearch === '' || item.sku.toLowerCase().includes(productSearch.toLowerCase()) || item.name.toLowerCase().includes(productSearch.toLowerCase());
        const matchesCategory = category === -1 || item.categoryId === category;
        return matchesSearchText && matchesCategory;
      });
      setFilteredItems(filtered);
    }
  };

  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    setLoading(true);
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('deleteItemFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteItemFail') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItemIds((prev) => [...prev, itemId]);
    } else {
      setSelectedItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const handleDeleteSelectedItems = () => {
    if (!orderDetails || selectedItemIds.length === 0) return;
    const selectedSKUs = orderDetails.items
      .filter(item => selectedItemIds.includes(item.id))
      .map(item => item.sku)
      .join(', ');
    setDialogState({
      open: true,
      title: t('deleteModalTitle'),
      message: `${t('deleteMultiModalMessage')}: ${selectedSKUs}?`,
      confirmAction: () => handleConfirmMultipleDeletion(),
    });
  };

  const handleConfirmMultipleDeletion = async () => {
    if (!orderDetails) return;
    setLoading(true);
    try {
      const request: DeleteOrderItemsRequest = {
        orderId: orderDetails.id,
        items: selectedItemIds,
      };
      const result = await client.deleteOrderItems(selectedStore?.storeNumber, request);
      if (result.success) {
        handleSnackbarOpen(t('multipleDeleteSuccess'), 'success');
        setSelectedItemIds([]);
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('multipleDeleteError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('multipleDeleteError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState(prev => ({ ...prev, open: false }));
    }
  };

  const handleConfirmDeletion = async (itemId: number, itemNumber: number) => {
    if (itemId !== null) {
      if (itemQuantities[itemNumber]) {
        setItemQuantities((prev) => {
          const newQuantities = JSON.parse(JSON.stringify(prev)) as Record<number, number>;
          delete newQuantities[itemNumber];

          return newQuantities;
        });
      }
      setDialogState(prev => ({ ...prev, open: false }));
      await handleDeleteOrderItem(Number(id), itemId);
    }
  };

  const handleOpenDialogForDeletion = (itemId: number, itemNumber: number) => {
    setDialogState({
      open: true,
      title: t('deleteModalTitle'),
      message: t('deleteModalMessage'),
      confirmAction: () => handleConfirmDeletion(itemId, itemNumber),
    });
  };

  const handleConfirmAction = async () => {
    if (dialogState.confirmAction) {
      await dialogState.confirmAction();
    }
    setDialogState(prev => ({ ...prev, open: false }));
  };

  const handleCancelDialog = () => {
    setDialogState(prev => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setProductSearch('');
    setCategory(-1);
    if (orderDetails) {
      setFilteredItems(orderDetails.items);
    }
  };

  const handleSaveChanges = async () => {

    if (!orderDetails) return;

    setLoading(true);

    const updatedItems = orderDetails.items.filter(x => x.id > 0 || x.id === 0 && itemQuantities[x.itemId] > 0).map(item => ({
      id: item.itemId,
      quantity: itemQuantities[item.itemId] ?? item.quantity,
    }));

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFD,
    };

    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        try {
          const shippingResult = await client.setShippingDetails(
            selectedStore?.storeNumber ?? '',
            orderDetails?.id.toString() ?? '',
            {
              deliveryCharge: parseInt(deliveryCharge) || 0, 
              deliveryMethod: deliveryMethod,
              deliveryMethodId: selectedShippingMethodId ?? '',
              orderStatus: orderDetails?.statusId.toString() ?? '',
              deliveryOptions: deliveryOptions,
              country,
              location,
              containerType: Number(containerType),
              palletType: Number(palletType),
            },
          );

          if (shippingResult.success) {
            setModifiedItems(new Set());
            handleSnackbarOpen(t('updateSuccess'), 'success');
            await fetchOrderDetails();
          } else {
         
            setModifiedItems(new Set());
            handleSnackbarOpen(t('updateSuccess') + ' ' + t('shippingUpdateWarning', 'However, there was an issue saving shipping details.'), 'warning');
            await fetchOrderDetails();
          }
        } catch (shippingError) {
        
          console.error('Failed to save shipping details:', shippingError);
          setModifiedItems(new Set());
          handleSnackbarOpen(t('updateSuccess') + ' ' + t('shippingUpdateError', 'However, shipping details could not be saved.'), 'warning');
          await fetchOrderDetails();
        }
      } else {
        setWarningMessage(result.message);
        setShowQuantityWarningDialog(true);
        fixQuantity();
        handleSnackbarOpen(t('updateError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderCopyConfirm = async (orderId: number) => {
    setIsCopyOrderModalOpen(false);
    setLoading(true);
    try {
      const result = await client.copyOrder(selectedStore?.storeNumber, orderDetails?.id.toString(), orderId.toString());
      if (result.success) {
        handleSnackbarOpen(t('copySuccess', 'Order copied successfully'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('copyError', 'Failed to copy order'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('copyError', 'Failed to copy order') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialogForApproveOrder = () => {
    setCheckoutDialogData({
      title: t('orderConfirmationTitle', 'Order Confirmation'),
      message: t('checkoutSubText'),
      confirmAction: () => handleApproveOrder(),
    });
    setIsCheckoutDialogOpen(true);
  };

  const handleApproveOrder = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('approveError'), 'error');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (orderDetails.status === OrderStatus.Amended) {
        result = await client.approveAmendment(selectedStore.storeNumber, orderDetails.id.toString());
      } else {
        result = await client.approveOrder(selectedStore.storeNumber, orderDetails.id.toString());
      }
      if (result.success) {
        handleSnackbarOpen(t('approveSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('approveFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('approveError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectButtonClick = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
  };
  const handleRejectOrder = async () => {
    setRejectDialogOpen(false);
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('rejectError'), 'error');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (orderDetails.status === OrderStatus.Amended) {
        result = await client.rejectAmendment(selectedStore.storeNumber, orderDetails.id.toString());
      } else {
        result = await client.rejectOrder(selectedStore.storeNumber, orderDetails.id.toString());
      }
      if (result.success) {
        handleSnackbarOpen(t('rejectSuccess'), 'success');
        setActiveOrder(null);
        setActiveContainerData(null);
      } else {
        handleSnackbarOpen(t('rejectFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('rejectError'), 'error');
    } finally {
      setLoading(false);
      navigate('/orders');
    }
  };

  const handleExitAndSave = async () => {
    setIsExitAndSaveClicked(true);
    if (!loading) {
      if (orderDetails?.status === OrderStatus.Pending) {
        await handleSaveChanges();
      }
      navigate('/orders');
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDeleteOrderDialog = () => {
    setDialogState({
      open: true,
      title: t('deleteOrderModalTitle', 'Delete Order'),
      message: t('deleteOrderModalMessage', 'Are you sure you want to delete this order? This action cannot be undone.'),
      confirmAction: handleConfirmDeleteOrder,
    });
  };

  const handleConfirmDeleteOrder = async () => {
    if (!selectedStore || !orderDetails?.id) {
      handleSnackbarOpen(t('deleteOrderError', 'Could not delete order. Store or Order ID missing.'), 'error');
      return;
    }
    setLoading(true);
    try {
      const result = await client.deleteOrder(selectedStore.storeNumber, orderDetails.id.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteOrderSuccess', 'Order deleted successfully.'), 'success');
        setActiveOrder(null);
        setActiveContainerData(null);
        navigate('/orders');
      } else {
        handleSnackbarOpen(result.message || t('deleteOrderFail', 'Failed to delete order.'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteOrderError', 'An error occurred while deleting the order.') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState(prev => ({ ...prev, open: false }));
    }
  };

  const handleDownloadSpreadsheet = async () => {
    if (!selectedStore || !orderDetails?.id) {
      handleSnackbarOpen(t('downloadSpreadsheetError', 'Could not download spreadsheet. Store or Order ID missing.'), 'error');
      return;
    }

    setIsDownloadingSpreadsheet(true);
    try {
      const result = await client.downloadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString());
      if (result.success && result.data) {
        // Decode base64 content and create blob
        const binaryString = atob(result.data.fileData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        exportFile(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
        handleSnackbarOpen(t('downloadSpreadsheetSuccess'), 'success');
      } else {
        handleSnackbarOpen(result.message || t('downloadSpreadsheetError'), 'error');
      }
    } catch (error) {
      console.error('Failed to download spreadsheet:', error);
      handleSnackbarOpen(t('downloadSpreadsheetError'), 'error');
    } finally {
      setIsDownloadingSpreadsheet(false);
    }
  };

  const handleUploadSpreadsheet = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedStore || !orderDetails?.id) {
      if (!file) {
        handleSnackbarOpen(t('selectFile', 'Please select a file.'), 'error');
      } else {
        handleSnackbarOpen(t('uploadSpreadsheetError', 'Could not upload spreadsheet. Store or Order ID missing.'), 'error');
      }
      return;
    }

    setIsUploadingSpreadsheet(true);
    try {
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const request = {
        fileName: file.name,
        fileData: base64Content,
      };

      const result = await client.uploadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString(), request);
      if (result.success) {
        handleSnackbarOpen(t('uploadSpreadsheetSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(result.message || t('uploadSpreadsheetError'), 'error');
      }
    } catch (error) {
      console.error('Failed to upload spreadsheet:', error);
      handleSnackbarOpen(t('uploadSpreadsheetError'), 'error');
    } finally {
      setIsUploadingSpreadsheet(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateShippingDetailsContextForActiveOrder = (method: string, charge: string) => {
    setShippingDetails({
      deliveryMethod: method,
      deliveryCharge: charge,
    });
  };
  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {
      
        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data) {
          const data = result.data;
          const chargeValue = data.deliveryCharge || 0;
          if (data.isDisabled) {
            setDeliveryMethod('');
            setSelectedShippingMethodId('');
            setDeliveryCharge('0');
        
          } else {
            setDeliveryMethod(data.deliveryMethod);
            setDeliveryCharge(chargeValue.toString());

            if (data.deliveryMethodId) {
              setSelectedShippingMethodId(data.deliveryMethodId);
            } else if (data.deliveryOptions && data.deliveryOptions.length > 0) {
           
              const matchingOption = data.deliveryOptions.find(option => 
                option.name === data.deliveryMethod,
              );
              if (matchingOption) {
                setSelectedShippingMethodId(matchingOption.shippingMethodId.toString());
              }
            }
          }

          setCountry(data.country);
          setLocation(data.location);
          setContainerType(data.containerType ?? '');
          setPalletType(data.palletType ?? '');
          setDeliveryOptions(data.deliveryOptions || []);
          
          setShippingDetails({
            deliveryMethod: data.deliveryMethod,
            deliveryCharge: chargeValue.toFixed(2),
          });
        }

      } catch (error) {
        console.error('Failed to fetch shipping details', error);
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id) {
      fetchShippingDetails();
    }
  }, [selectedStore?.storeNumber, orderDetails?.id]);

  
  useEffect(() => {
    const showShippingWarning = async () => {
      if (tabValue !== OrderDetailsEnum.Shipping) return;
    
      try {
        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data && result.data.isDisabled) {
          handleSnackbarOpen('Please select an active delivery method.', 'warning');
        }
      } catch (error) {
        console.error('Failed to check shipping status for tab', error);
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id && tabValue === OrderDetailsEnum.Shipping) {
      showShippingWarning();
    }
  }, [tabValue, selectedStore?.storeNumber, orderDetails?.id]);

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];
  const quantityError = (item: OrderItemDetails): string | null => {
    const quantity = itemQuantities[item.itemId] ?? item.quantity;

    if (item.id === 0) return null;

    if (quantity < 0) {
      return t('negativeQuantityNotAllowed', 'Negative quantity is not allowed');
    }

    if ((item.maxQuantity != null) && quantity > item.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: OrderItemDetails): boolean => {
    return quantityError(item) != null;
  };

  const anyErrors = (): boolean => {
    return filteredItems.some(x => isQuantityError(x));
  };
  const fixQuantity = () => {
    const itemQuantities2 = { ...itemQuantities };
    for (const key in itemQuantities2) {
      const value = itemQuantities2[key];
      const maxQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.maxQuantity;
      const minQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.minQuantity;

      if (value < 0)
        itemQuantities2[key] = 0;
      if (maxQuant && value > maxQuant)
        itemQuantities2[key] = maxQuant;
      if (minQuant && value < minQuant)
        itemQuantities2[key] = minQuant;
    }
    setItemQuantities(itemQuantities2);
  };

  const handleOpenDialogForCheckoutOrder = (orderId: number) => {
    setCheckoutDialogData({
      title: t('orderConfirmationTitle', 'Order Confirmation'),
      message: t('checkoutSubText'),
      confirmAction: () => onCheckoutOrder(orderId),
    });
    setIsCheckoutDialogOpen(true);
  };

  const handleOpenDialogForRejectOrder = () => {
    setCheckoutDialogData({
      title: t('rejectOrderTitle', 'Reject Order'),
      message: t('rejectSubText'),
      confirmAction: () => handleRejectOrder(),
    });
    setIsCheckoutDialogOpen(true);
  };

  const onCheckoutOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await client.checkoutOrder(selectedStore?.storeNumber, orderId.toString());
      if (response.success) {
        handleSnackbarOpen(t('orderCheckedOut'), 'success');
        await fetchOrderDetails();
      }
      else {
        setWarningMessage(response.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
      navigate('/orders');
    }

  };

  const handleDeliveryDateChange = async (date: Dayjs | null) => {
    setSelectedDeliveryDate(date);
    if (date && orderDetails?.id) {
      try {
        const apiDateFormat = date.format('DD-MM-YYYY');
        const result = await client.updateOrderDeliveryDate(
          selectedStore?.storeNumber,
          orderDetails.id.toString(),
          apiDateFormat,
        );
        if (result.success) {
          handleSnackbarOpen(t('deliveryDateUpdated', 'Delivery date updated'), 'success');
          await fetchOrderDetails();
        } else {
          handleSnackbarOpen(t('deliveryDateUpdateFailed', 'Failed to update delivery date'), 'error');
        }
      } catch (error) {
        handleSnackbarOpen(t('deliveryDateUpdateFailed', 'Failed to update delivery date') + ': ' + error, 'error');
      }
    }
  };
  return (
    <PageArea>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls,.csv"
        onChange={handleFileUpload}
      />

      <Grid item
        xs={12}
        mb={10}
        sx={{ textAlign: 'left' }}>
        <BackButton
          handleClick={handleExitAndSave}
          title={t('exitAndSave')}
          isLink={false}
        />
      </Grid>

      <Grid container>
        <Grid item
          container
          xs={12}>          <Grid item
            container
            alignItems={'end'}
            justifyContent="space-between">
            <Grid item
              xs={12}
              md="auto">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PageTitle>{t('order')} {id}</PageTitle>
                {orderDetails && <OrderStatusChip orderStatus={orderDetails.status} />}
              </Box>
            </Grid>
            <Grid item
              xs={12}
              md="auto"
              sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'stretch', md: 'flex-end' } }}
            >
              {canDeleteOrder() && (
                <Button
                  variant="bad"
                  disabled={loading}
                  size="lg"
                  onClick={handleOpenDeleteOrderDialog}
                  startIcon={<DeleteIcon />}
                  sx={{ minWidth: '250px', maxWidth: '250px' }}
                >
                  {t('deleteOrder', 'Delete Order')}
                </Button>
              )}
              {canCheckoutOrder() && (
                <Button
                  variant="primary"
                  disabled={
                    loading || filteredItems.length === 0 || anyErrors()
                  }
                  size="lg"
                  onClick={() => handleOpenDialogForCheckoutOrder(orderDetails?.id ?? 0)}
                  sx={{ minWidth: '250px', maxWidth: '250px' }}
                >
                  {t('checkoutOrder')}
                </Button>
              )}
              {canSaveChanges() && (
                <Button
                  variant="primary"
                  disabled={loading || anyErrors()}
                  size="lg"
                  onClick={handleSaveChanges}
                  sx={{ minWidth: '250px', maxWidth: '250px' }}
                >
                  {t('save')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item
          container
          spacing={3}
          alignItems="center"
          xs={12}
          p={3}>
          <Grid item
            xl={3}
            lg={3}
            md={4}
            sm={6}
            xs={12}>
            <TextField
              fullWidth
              size="small"
              label={t('orderType')}
              disabled
              value={t(OrderTypeTranslationMap[orderType as keyof typeof OrderTypeTranslationMap]) || orderType}
            />
          </Grid>
          {displayDeliveryDate() && (
            <Grid item
              xl={3}
              lg={3}
              md={4}
              sm={6}
              xs={12}>
              {orderDetails?.amendmentDetails && orderDetails?.status === 'Amended' ? (
                <Tooltip
                  title={orderDetails.amendmentDetails.replace(/(\d{2}\/\d{2}\/\d{4})\s+\d{2}:\d{2}:\d{2}/g, '$1')}
                  arrow
                  placement="top"
                >
                  <div>
                    <DatePickerLocalized
                      sx={{
                        backgroundColor: '#FFFAEB',
                      }}
                      label={t('deliveryDate')}
                      valueForDateCalendar={selectedDeliveryDate}
                      useCalendar={false}
                      format="DD/MM/YYYY"
                      disabled={!canAmendOrderDate()}
                      onChangeDateCalendar={handleDeliveryDateChange}
                      minDate={dayjs()}
                    />
                  </div>
                </Tooltip>
              ) : (
                <DatePickerLocalized
                  label={t('deliveryDate')}
                  valueForDateCalendar={selectedDeliveryDate}
                  useCalendar={false}
                  format="DD/MM/YYYY"
                  disabled={!canAmendOrderDate()}
                  onChangeDateCalendar={handleDeliveryDateChange}
                  minDate={dayjs()}
                />
              )}
            </Grid>
          )}
          {(orderDetails?.status === OrderStatus.Pending || orderDetails?.status === 'Review') && (
            <Grid item
              xs
              sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
            >
              <Tooltip title={t('downloadSpreadsheetTooltip')}>
                <Button
                  variant="secondary"
                  disabled={loading || isDownloadingSpreadsheet}
                  size="lg"
                  onClick={handleDownloadSpreadsheet}
                  startIcon={<DownloadIcon />}
                  sx={{ minWidth: '120px' }}
                >
                  {isDownloadingSpreadsheet ? t('loading', 'Loading...') : t('downloadSpreadsheet')}
                </Button>
              </Tooltip>
              <Tooltip title={t('uploadSpreadsheetTooltip')}>
                <Button
                  variant="secondary"
                  disabled={loading || isUploadingSpreadsheet || !canEditOrder()}
                  size="lg"
                  onClick={handleUploadSpreadsheet}
                  startIcon={<UploadIcon />}
                  sx={{ minWidth: '120px' }}
                >
                  {isUploadingSpreadsheet ? t('loading', 'Loading...') : t('uploadSpreadsheet')}
                </Button>
              </Tooltip>
            </Grid>
          )}
        </Grid>
        <Grid item
          container
          xs={12}
          mt={5}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
          }}>
          <Grid container
            sx={{
              padding: '20px',
            }}
            spacing={2}>
            <Grid item
              xl={12}
              md={12}
              xs={12}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: 0 }}
            >
              <Grid container
                spacing={2}>
                <Grid item
                  xl={9}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid item>
                      <ContainerPlanningView containerData={containerData}
                        currencyCode={currencyCode ?? ''} />
                    </Grid>
                  </Box>
                </Grid>
                <Grid item
                  xl={3}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid container
                      spacing={2}>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{ mt: 5, mb: 1, px: 2 }}>
              <Typography
                color="textSecondary"
                sx={{ fontSize: '12px', fontStyle: 'italic', lineHeight: 1.5 }}>
                {t('subjectToChange')}
              </Typography>
            </Grid>
          </Grid>
          <Grid item
            xs={12}>
            <Divider />
          </Grid>

          <Tabs value={tabValue}
            onChange={handleChange}>
            <Tab label={t('basket')}
              value={OrderDetailsEnum.Basket} />
            <Tab label={t('shipping')}
              value={OrderDetailsEnum.Shipping} />
            <Tab label={t('documents')}
              value={OrderDetailsEnum.Documents} />
          </Tabs>

          {tabValue === OrderDetailsEnum.Basket && (
            <Grid container>
              <Grid item
                container
                sx={{
                  px: 4,
                  py: 6,
                }}
                xs={12}
                spacing={5}>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    fullWidth
                    size='small'
                    placeholder={t('searchByItemNameOrSKU')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon><SearchSm /></SvgIcon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <Autocomplete
                    size='small'
                    options={autocompleteOptions}
                    getOptionLabel={(option) => isOrderCategory(option.name) ? t(OrderCategoriesTranslationMap[option.name as keyof typeof OrderCategoriesTranslationMap]) : option.name}
                    value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
                    onChange={(event, newValue) => {
                      setCategory(newValue ? newValue.id : -1);
                    }}
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('category')}
                        placeholder={t('choose')} />
                    )}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    variant="bad"
                    disabled={loading || selectedItemIds.length === 0 || !canEditOrder()}
                    size="lg"
                    fullWidth
                    onClick={handleDeleteSelectedItems}
                    startIcon={<DeleteIcon />}
                  >
                    {t('deleteSelected')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button fullWidth
                    variant="secondary"
                    disabled={loading}
                    onClick={clearFilters}
                    size="lg">
                    {t('clearFilters')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button fullWidth
                    variant="primary"
                    disabled={loading}
                    onClick={handleSearch}
                    size="lg"
                    startIcon={<SearchSm />}>
                    {t('search')}
                  </Button>
                </Grid>
              </Grid>
              <Grid item
                xs={12}>
                {loading ? (
                  <LoadingWheel />
                ) : (
                  <TableContainer
                    component={'div'}
                    sx={{
                      overflowX: 'auto',
                      maxWidth: '100%',
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="a table"
                      sx={{
                        minWidth: '700px',
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={2}
                            sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                            {t('itemDetails')}
                          </TableCell>
                          <TableCell>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '10%' }}>
                            {t('ordered')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '9%' }}>
                            {t('cost')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '9%' }}>
                            {t('actions')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredItems.map((item) => {
                          const quantity = itemQuantities[item.itemId] ?? item.quantity;
                          totalCost += item.price * quantity;
                          return (
                            <TableRow
                              key={item.itemId}
                              hover
                              onFocus={() => handleItemFocus(item.itemId)}
                              sx={{
                                ...((item.amendmentDetails || parseInt(item.priceOverrideReason ?? '') > 0) && orderDetails?.status === 'Amended'
                                  ? { borderLeft: '4px solid #FFD600 !important' } // force yellow border
                                  : {}),
                              }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '20%' }}>
                                <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Grid item
                                    xs="auto">
                                    <Checkbox
                                      checked={selectedItemIds.includes(item.id)}
                                      onChange={(event) =>
                                        handleCheckboxChange(item.id, event.target.checked)
                                      }
                                      disabled={
                                        loading || !canDeleteOrderItem()
                                      }
                                    />
                                  </Grid>
                                  <Grid item
                                    sx={{ marginRight: '15px' }}
                                    onClick={
                                      hasPermissionTo(['ProductDetailView'])
                                        ? () => navigate(`/ofd/products/${item.sku}`, {
                                          state: {
                                            orderId: orderDetails?.id,
                                            fromOrderPage: true,
                                            quantity: itemQuantities[item.itemId] ?? item.quantity,
                                            canChangeOrderQuantity: canChangeOrderQuantity(),
                                            orderItemId: item.id,
                                          },
                                        })
                                        : undefined
                                    }>
                                    <Box
                                      style={{
                                        width: '100px',
                                        height: isLargeScreen ? '75px' : '100px',
                                        justifyContent: 'center',
                                        backgroundColor: (item?.thumbnailUrl ?? '') != '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        border: '1px solid #ddd',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        imageRendering: '-webkit-optimize-contrast',
                                        backgroundImage: `url(${item?.thumbnailUrl && item?.thumbnailUrl.trim() !== '' ? item.thumbnailUrl : NoProductImage})`,
                                      }}
                                    />
                                  </Grid>


                                  <Grid item
                                    sx={{ width: '65%' }}
                                  >
                                    <TextWithOverflowTooltip text={
                                      item.name + (item.amendmentDetails && orderDetails?.status === 'Amended'
                                        ? ` (${item.amendmentDetails})`
                                        : '')
                                    } />
                                    <TextWithOverflowTooltip text={item.sku} />

                                    <TextWithOverflowTooltip text={isOrderCategory(item.category)
                                      ? t(OrderCategoriesTranslationMap[item.category as keyof typeof OrderCategoriesTranslationMap])
                                      : item.category} />
                                  </Grid>
                                </Grid>
                              </TableCell>

                              <TableCell sx={{ width: '8%' }}>
                                {(showMessageWhenNoCost || (!item.priceOverrideReason && item.price === 0)) ?
                                  <TextWithOverflowTooltip text='' />
                                  :
                                  (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {item.priceOverrideOriginalPrice
                                        ? (
                                          <>
                                            <Tooltip
                                              title={
                                                PriceOverrideReasonText[Number(item.priceOverrideReason)] ||
                                                item.priceOverrideReason ||
                                                ''
                                              }
                                            >
                                              <span style={{
                                                textDecoration: 'line-through',
                                                color: theme.palette.text.disabled,
                                                cursor: item.priceOverrideReason ? 'pointer' : 'default',
                                              }}>
                                                {applyRoundAndFormat(item.priceOverrideOriginalPrice, currencyCode)}
                                              </span>
                                            </Tooltip>
                                            <span style={{ marginLeft: 6 }}>
                                              {applyRoundAndFormat(item.price, currencyCode)}
                                            </span>
                                          </>
                                        )
                                        : applyRoundAndFormat(item.price, currencyCode)
                                      }
                                      {orderDetails?.status === OrderStatus.Review && hasPermissionTo(['PriceOverrideWrite']) &&
                                        <IconButton
                                          size="small"
                                          disabled={loading || !canEditOrder()}
                                          onClick={() => handleEditClick(item)}
                                          sx={{ ml: 1 }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      }
                                      {item.priceOverrideOriginalPrice == undefined && item.gbpPrice !== undefined && item.price !== item.gbpPrice && (
                                        <span
                                          style={{
                                            color: theme.palette.text.disabled,
                                            fontStyle: 'italic',
                                            marginLeft: 6,
                                            fontSize: '0.95em',
                                          }}
                                        >
                                          ({applyRoundAndFormat(item.gbpPrice, 'GBP')})
                                        </span>
                                      )}
                                    </Box>
                                  )}
                              </TableCell>
                              <TableCell>
                                <TextWithOverflowTooltip text={`${t('orderableUOM')} ${item.orderableUom} (${item.packSize}${item.primaryUom})`} />
                                <TextWithOverflowTooltip text={`${t('primaryUOM')} ${item.primaryUom}`} />
                                <Tooltip title={`${t('stockOnHand')}: ${item.stockOnHand}`}
                                  arrow>
                                  <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                                    {t('stockOnHandABR')} {item.stockOnHand}
                                  </Box>
                                </Tooltip>
                                <Tooltip title={`${t('stockOnOrder')}: ${item.stockOnOrder}`}
                                  arrow>
                                  <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                                    {t('StockOnOrderABR')} {item.stockOnOrder}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center"
                                sx={{ verticalAlign: 'middle' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  autoComplete='off'
                                  error={isQuantityError(item)}
                                  helperText={quantityError(item)}
                                  inputProps={{ min: 0 }}
                                  disabled={loading || !canChangeOrderQuantity()}
                                  value={itemQuantities[item.itemId] ?? item.quantity}
                                  onChange={(e) => {
                                    const value = Math.abs(parseInt((e.target as HTMLInputElement).value || '0'));

                                    if (/^\d*$/.test(e.target.value)) {
                                      setItemQuantities(prev => ({ ...prev, [item.itemId]: value }));

                                      if (!modifiedItems.has(item.itemId)) {
                                        setModifiedItems(prev => new Set(prev).add(item.itemId));
                                      }
                                    }
                                  }}
                                  onBlur={async (e) => {
                                    let value = Math.abs(parseInt((e.target as HTMLInputElement).value || '0'));
                                    if (isNaN(value) || value < 0) value = 0;
                                    if (value === 0) {
                                      await handleConfirmDeletion(item.id, item.itemId);
                                    } else {
                                      await autosaveItem(item.itemId);
                                    }
                                  }}
                                  onFocus={e => e.target.select()}
                                  sx={{
                                    width: '100px',
                                    input: {
                                      textAlign: 'center',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center"
                                sx={{ verticalAlign: 'middle' }}>
                                {applyRoundAndFormat(item.price * (itemQuantities[item.itemId] ?? item.quantity), currencyCode)}
                              </TableCell>
                              <TableCell align="center"
                                sx={{ verticalAlign: 'middle' }}>
                                <Tooltip title={item.id === 0 ? t('cannotRemoveItem') : ''}>
                                  <span>
                                    <IconButton
                                      color="error"
                                      disabled={loading || !canDeleteOrderItem() || item.id === 0}
                                      onClick={() => handleOpenDialogForDeletion(item.id, item.itemId)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        },
                        )}

                        {filteredItems.length > 0 && (
                          <>
                            <TableRow>
                              <TableCell colSpan={4}
                                align="right"
                                sx={{ fontWeight: 'normal' }}>
                                {t('subtotal')}
                              </TableCell>
                              <TableCell align="center"
                                sx={{ fontWeight: 'normal' }}>
                                {applyRoundAndFormat(totalCost, currencyCode)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={4}
                                align="right"
                                sx={{ fontWeight: 'normal' }}>
                                {t('deliveryCharge')}
                              </TableCell>
                              <TableCell align="center"
                                sx={{ fontWeight: 'normal' }}>
                                {applyRoundAndFormat(parseFloat(deliveryCharge || '0'), currencyCode)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                            <TableRow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                              <TableCell colSpan={4}
                                align="right"
                                sx={{ fontWeight: 'bold' }}>
                                {t('totalCost')}
                              </TableCell>
                              <TableCell align="center"
                                sx={{ fontWeight: 'bold' }}>
                                {applyRoundAndFormat(totalCost + parseFloat(deliveryCharge || '0'), currencyCode)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </>
                        )}

                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
              <Grid item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 7, mb: 5, px: 7 }}>
                {canDeleteOrderItem() && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      sx={{ px: 3, py: 1.5 }}
                      onClick={() => navigate(`/products-list/${orderDetails?.id}`)}>
                      {t('browseCatalogue')}
                    </Button>
                    <Button
                      sx={{ px: 3, py: 1.5 }}
                      disabled={loading || filteredItems.length === 0 || anyErrors()}
                      onClick={() => handleOpenDialogForClearBasket(orderDetails?.id ?? 0)}>
                      {t('clearBasket')}
                    </Button>
                  </Box>
                )}
                {canApproveOrder() && (
                  <Box>
                    <Button
                      variant="bad"
                      sx={{ ml: 2, px: 3, py: 1.5 }}
                      startIcon={<CancelIcon />}
                      disabled={loading || !hasPermissionTo(['OrdersApprove'])}
                      onClick={() => handleRejectButtonClick()}>
                      {t('reject')}
                    </Button>
                    <Button
                      variant="primary"
                      sx={{ ml: 2, px: 3, py: 1.5 }}
                      startIcon={<CheckIcon />}
                      disabled={loading || !hasPermissionTo(['OrdersApprove'])}
                      onClick={() => handleApproveOrder()}>
                      {t('approve')}
                    </Button>
                  </Box>
                )}
                {(canRejectAmendedOrder() || canApproveAmendedOrder()) && (
                  <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
                    {canRejectAmendedOrder() && (
                      <Button
                        variant="bad"
                        sx={{ px: 3, py: 1.5 }}
                        startIcon={<CancelIcon />}
                        disabled={loading || !hasPermissionTo(['OrdersRejectConfirmation'])}
                        onClick={() => {
                          handleOpenDialogForRejectOrder();
                        }}>
                        {t('reject')}
                      </Button>
                    )}
                    {canApproveAmendedOrder() && (
                      <Button
                        variant="primary"
                        sx={{ px: 3, py: 1.5 }}
                        startIcon={<CheckIcon />}
                        disabled={loading || !hasPermissionTo(['OrdersApproveConfirmation'])}
                        onClick={() => {
                          handleOpenDialogForApproveOrder();
                        }}>
                        {t('approve')}
                      </Button>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === OrderDetailsEnum.Shipping && (

            <Grid container
              spacing={3}
              sx={{ mt: 3, ml: 3, mr: 2 }}>
              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('deliveryMethod')}
                  sx={{ mb: 3 }}
                  value={selectedShippingMethodId}
                  disabled={!canUpdateDeliveryMethod()}
                  onChange={e => {
                    const newShippingMethodId = e.target.value;
                    setSelectedShippingMethodId(newShippingMethodId);

                    const selectedOption = deliveryOptions.find(option => 
                      option.shippingMethodId.toString() === newShippingMethodId,
                    );

                    if (selectedOption) {
                      setDeliveryMethod(selectedOption.name);
                      const wholeNumberCharge = Math.round(selectedOption.charge); 
                      setDeliveryCharge(wholeNumberCharge.toString()); // Display as whole number
                      updateShippingDetailsContextForActiveOrder(selectedOption.name, wholeNumberCharge.toString());
                      autosaveShippingDetails(selectedOption.shippingMethodId, wholeNumberCharge);
                    }
                  }}
                >
                  {deliveryOptions.map((option) => (
                    <MenuItem key={option.shippingMethodId}
                      value={option.shippingMethodId.toString()}>
                      {option.name} ({Math.round(option.charge)}) 
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('Override Delivery Charge')}
                  value={deliveryCharge}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setDeliveryCharge(val);
                    }
                  }}
                  onBlur={e => {
                    const numValue = parseInt(e.target.value) || 0; 
                    setDeliveryCharge(numValue.toString()); 
                    updateShippingDetailsContextForActiveOrder(deliveryMethod, numValue.toString());
                    autosaveShippingDetails(selectedShippingMethodId, numValue);
                  }}
                  disabled={!canUpdateDeliveryCharge()}
                  sx={{ mb: 3 }}
                  inputProps={{ 
                    min: 0, 
                    inputMode: 'numeric', 
                  }}
                  onKeyDown={e => {
                    if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  fullWidth
                  label={t('country')}
                  sx={{ mb: 3 }}
                  value={country}
                  disabled={deliveryMethod === 'Ex Works' || loading || !canEditOrder()}
                  onChange={e => {
                    setCountry(e.target.value);
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item
                xs={12}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  fullWidth
                  label={t('location')}
                  multiline
                  rows={3}
                  sx={{ mb: 3 }}
                  value={location}
                  disabled={deliveryMethod === 'Ex Works' || loading || !canEditOrder()}
                  onChange={e => {
                    setLocation(e.target.value);
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('container')}
                  sx={{ mb: 3 }}
                  value={containerType}
                  disabled={loading || !canChangeContainer()}
                  onChange={e => {
                    const newValue = e.target.value;
                    setContainerType(newValue);
                    autosaveShippingDetails(undefined, undefined, newValue);
                  }}
                >
                  {containerSizes.map((option) => (
                    <MenuItem key={option.key}
                      value={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('pallet')}
                  disabled={loading || !canChangePalletType()}
                  sx={{ mb: 3 }}
                  value={palletType}
                  onChange={e => {
                    const newValue = e.target.value;
                    setPalletType(newValue);
                    autosaveShippingDetails(undefined, undefined, undefined, newValue);
                  }}
                >
                  <MenuItem value={1}>UK</MenuItem>
                  <MenuItem value={2}>EU</MenuItem>
                </TextField>
              </Grid>
            </Grid>

          )}

          {tabValue === OrderDetailsEnum.Documents &&
            <Grid item
              xs={12}
              sx={{ px: { xs: 0, md: 4 }, mt: 3 }}>

              <OrderDocuments
                products={filteredItems}
                productDocuments={productDocuments}
                orderDocuments={orderDetails?.documents}
                orderStatus={orderDetails?.status}
                download={(documentId: number) => downloadFile(documentId, downloadProductDocument)}
                loadData={loadData}
              />
            </Grid>
          }
        </Grid>
      </Grid>
      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <ActionConfirmationDialog
        open={isCheckoutDialogOpen}
        icon={<WarningAmberIcon />}
        variant="warning"
        title={checkoutDialogData.title}
        message={checkoutDialogData.message}
        confirmButtonText={t('checkoutConfirm')}
        cancelButtonText={t('checkoutCancel')}
        onConfirm={() => {
          checkoutDialogData.confirmAction();
          setIsCheckoutDialogOpen(false);
        }}
        onCancel={() => setIsCheckoutDialogOpen(false)}
      />
      <ConfirmationDialog
        open={dialogState.open}
        icon={<DeleteIcon />}
        title={dialogState.title}
        message={dialogState.message}
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelDialog}
      />
      <CopyOrderModal
        currentOrderId={orderDetails?.id ?? 0}
        orderTypeId={orderDetails?.orderTypeId ?? 0}
        open={isCopyOrderModalOpen}
        onClose={() => setIsCopyOrderModalOpen(false)}
        onConfirm={handleOrderCopyConfirm}
      />
      <ItemPriceOverrideModal
        selectedItem={selectedOverrideItem}
        openModal={handleShowOverridePrice}
        onCancel={() => { setHandleShowOverridePrice(false); setSelectedOverrideItem(undefined); }}
        onConfirm={(editedItem) => updateItemPriceOverride(editedItem)}
        currencyCode={currencyCode}
      />
      <OrderAuditModal
        currentOrderId={orderDetails?.id ?? 0}
        open={isOrderAuditModalOpen}
        onClose={() => setIsOrderAuditModalOpen(false)}
      />
      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
      <LeavePageModal
        open={blocker.state === 'blocked' && modifiedItems.size > 0}
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleLeavePageModalCancel}
        onConfirmUrl='/store/order'
        leaveWarningContent={t('saveOnLeaveWarningContent')}
        leaveWarningTitle={t('saveOnLeaveWarningTitle')}
      />
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectDialogClose}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('rejectOrder')}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>
                {t('rejectOrderConfirmation', 'Are you sure you want to reject this order?')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleRejectDialogClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="bad"
            onClick={handleRejectOrder}>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea >
  );
};

export default OrderDetailsPageOfd;
